import { z } from "zod";

// Keep import relative for tsconfig.node compat
import { stringToBooleanSchema, tolerantStringToBooleanSchema } from "./lib/helpers/validation-helper";

const envBaseSchema = z.object({
  // Backends
  VITE_BACKOFFICE_BASE_URL: z.string().url(),
  VITE_KILI_BASE_URL: z.string().url(),
  VITE_HOSPITALITY_BASE_URL: z.string().url(),
  VITE_VOLTACALL_SERVICE_ID: z.coerce
    .number()
    .int()
    .describe("Voltacall identifier as an application (only for Backoffice-backend)"),
  VITE_SUBSCRIBER_BACKEND_BASE_URL: z.string().url().describe("Subscriber-backend (Mojo) base URL"),

  VITE_ESC_SLEEP_BEFORE_OPENING_LINK_MS: z.coerce
    .number()
    .int()
    .nullish()
    .describe("How long sleep before opening the ESC generated link (default 0)"),

  // Vite config
  VITE_USE_HTTPS: stringToBooleanSchema.optional(),
  VITE_SERVER_PORT: z.coerce.number().int().positive().optional(),
  // Temporary :tm: flag to enable/disable adding trailing slash to api calls
  // Relates to spring-boot 4.x migration on backoffice-backend
  VITE_ADD_TRAILING_SLASH: stringToBooleanSchema.optional(),

  // Zodios : API client behavior
  VITE_ZODIOS_VALIDATE: stringToBooleanSchema.describe(`
    If true, Zodios will validate the response of each API call against the Zod schema.
    If false, not only will there be no validation, but any zod mapping function (e.g z.date.coerce()) will **NOT** happen.
    `),

  // Observability
  VITE_SENTRY_ENABLED: tolerantStringToBooleanSchema,
  VITE_SENTRY_DSN: z.string().optional(),

  // Feature flags
  VITE_ENABLE_ACCOMMODATION_MANAGEMENT: stringToBooleanSchema,

  // External APIs

  // - Address lookup : Gouv API
  VITE_GOUV_ADDRESS_BASE_URL: z.string().url(),
  // - Address lookup : Google Maps API
  VITE_GOOGLE_MAPS_API_KEY: z.string().nonempty(),
});

export const googleCaptchaEnvSchema = z.union([
  z.object({
    VITE_IS_GOOGLE_CAPTCHA_ENABLED: z.literal("true").transform(() => true as const),
    VITE_GOOGLE_CAPTCHA_SITE_KEY: z.string().trim().min(1),
  }),
  z.object({
    VITE_IS_GOOGLE_CAPTCHA_ENABLED: z
      .enum(["false"])
      .nullish()
      .transform(() => false as const),
  }),
]);

export const envSchema = envBaseSchema.and(googleCaptchaEnvSchema);

export type Env = z.infer<typeof envSchema>;
